//@flow
import React, {useEffect, useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import {useLocation} from 'react-router-dom';
import {makeStyles, MenuItem, Typography} from '@material-ui/core';
import TiendadaRadioButton from '../../../../../../../elements/tiendada-radio-button';
import CustomSelect from '../../../../../../../elements/CustomSelect/CustomSelect';
import WhatsAppTemplateRenderer from '../../../../../../../elements/whatsapp-template-renderer';
import {
  formatStoreDeviceIdentifier,
  getServiceLabel,
  useStoreDevices,
  useWhatsappApiTemplates,
} from '../../../../../chat/utils';
import {getMultichatServiceIcon} from '../../../../../utils/service-icon';
import {TRIGGER_FIELDS} from '../../const';
import ResponseSection from '../ResponseSection';
import {getFormError} from '../../../../utils/formErrors';
import type {
  StoreDevice,
  TriggerInitProvider,
} from '../../../../../../../entities';
import {colors} from '../../../../../../../assets/styles/colors';
import {ENABLE_WHATSAPP_WEB_PROVIDER_SEARCH_PARAM} from '../../../../../../crm-campaigns/components/crm-campaign-form';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  label: {
    fontSize: '.875rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
  },
  deviceInput: {
    '&:checked + $deviceOption': {
      borderColor: colors.primary,
    },
    '&:checked + $deviceOption $deviceRadio': {
      border: `6px solid ${colors.primary}`,
    },
  },
  deviceOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '.75rem',
    cursor: 'pointer',
    padding: '.75rem 1rem',
    borderRadius: '.75rem',
    border: `1px solid ${colors.grey400}`,
    transition: 'border-color .2s ease-in-out',
    '&:hover': {
      borderColor: colors.primary,
    },
  },
  deviceRadio: {
    borderRadius: '50%',
    minWidth: '1.5rem',
    minHeight: '1.5rem',
    width: '1.5rem',
    height: '1.5rem',
    border: `1.25px solid ${colors.grey400}`,
    background: 'white',
    transition: 'border .2s ease-in-out',
    flexShrink: 0,
  },
  deviceIcon: {
    width: '2.5rem',
    height: '2.5rem',
    flexShrink: 0,
    '& img': {
      width: '100%',
      height: '100%',
    },
  },
  deviceInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
  },
  deviceName: {
    fontSize: '.875rem',
    fontWeight: 600,
    color: colors.grey900,
  },
  deviceIdentifier: {
    fontSize: '.8125rem',
    fontWeight: 500,
    color: colors.grey700,
  },
  fieldError: {
    fontSize: '.8125rem',
    color: colors.danger,
  },
  emptyState: {
    fontSize: '.875rem',
    color: colors.grey700,
  },
}));

const setValueOptions = {shouldDirty: true, shouldValidate: true};

export default function InitMessageSection() {
  const classes = useStyles();
  const location = useLocation();
  const {register, setValue, watch, errors} = useFormContext();
  const {storeDevices, isLoading: devicesLoading} = useStoreDevices();

  const searchParams = new URLSearchParams(location?.search);
  const enableWhatsappWebProvider = searchParams.has(
    ENABLE_WHATSAPP_WEB_PROVIDER_SEARCH_PARAM
  );

  const provider: ?TriggerInitProvider = watch(TRIGGER_FIELDS.provider);
  const deviceId = watch(TRIGGER_FIELDS.deviceId) || '';
  const templateName = watch(TRIGGER_FIELDS.templateName) || '';
  const {templates, isLoading: templatesLoading} = useWhatsappApiTemplates(
    provider === 'WHATSAPP_API' ? deviceId : undefined
  );

  useEffect(() => {
    // Validation is driven by validateTriggerForm — avoid RHF `required` on
    // setValue/reset fields (false invalid + empty messages on edit save).
    register(TRIGGER_FIELDS.provider);
    register(TRIGGER_FIELDS.deviceId);
    register(TRIGGER_FIELDS.templateName);
    register(TRIGGER_FIELDS.templateLang);
    register(TRIGGER_FIELDS.templateComponents);
  }, [register]);

  const connectedDevices = useMemo(
    () => storeDevices.filter(device => device.state === 'CONNECTED'),
    [storeDevices]
  );

  const hasWhatsappWeb = connectedDevices.some(
    device => device.service === 'WHATSAPP_WEB'
  );
  const hasWhatsappApi = connectedDevices.some(
    device => device.service === 'WHATSAPP_API'
  );

  // Keep WHATSAPP_WEB visible for hydrated edits when the flag is off so
  // existing triggers remain editable (campaigns re-appends the param on edit).
  const showWhatsappWebOption =
    (enableWhatsappWebProvider && hasWhatsappWeb) ||
    provider === 'WHATSAPP_WEB';

  const devicesForProvider = connectedDevices.filter(
    device => device.service === provider
  );

  const selectedTemplate = templates.find(
    template => template.name === templateName
  );

  const providerError = getFormError(errors, TRIGGER_FIELDS.provider);
  const deviceError = getFormError(errors, TRIGGER_FIELDS.deviceId);
  const templateError = getFormError(errors, TRIGGER_FIELDS.templateName);

  useEffect(() => {
    if (!provider || devicesLoading) return;

    // Keep hydrated deviceId until devices are actually available to validate.
    // Empty storeDevices while not loading can mean fetch hasn't started yet.
    if (storeDevices.length === 0) return;

    if (devicesForProvider.length === 1) {
      const onlyDeviceId = devicesForProvider[0].id;
      if (deviceId !== onlyDeviceId) {
        setValue(TRIGGER_FIELDS.deviceId, onlyDeviceId, setValueOptions);
      }
      return;
    }

    if (!deviceId) return;

    const stillValid = devicesForProvider.some(
      device => device.id === deviceId
    );
    if (!stillValid) {
      setValue(TRIGGER_FIELDS.deviceId, '', setValueOptions);
    }
  }, [
    deviceId,
    devicesForProvider,
    devicesLoading,
    provider,
    setValue,
    storeDevices.length,
  ]);

  const handleProviderChange = (nextProvider: TriggerInitProvider) => {
    setValue(TRIGGER_FIELDS.provider, nextProvider, setValueOptions);
    setValue(TRIGGER_FIELDS.deviceId, '', setValueOptions);

    if (nextProvider === 'WHATSAPP_WEB') {
      setValue(TRIGGER_FIELDS.templateName, '', setValueOptions);
      setValue(TRIGGER_FIELDS.templateLang, '', setValueOptions);
      setValue(TRIGGER_FIELDS.templateComponents, [], setValueOptions);
    } else {
      setValue(TRIGGER_FIELDS.response, '', setValueOptions);
      setValue(TRIGGER_FIELDS.attachments, [], setValueOptions);
      setValue(TRIGGER_FIELDS.savedDocuments, [], setValueOptions);
      setValue(TRIGGER_FIELDS.savedImages, [], setValueOptions);
      setValue(TRIGGER_FIELDS.savedVideos, [], setValueOptions);
    }
  };

  const handleDeviceSelect = (nextDeviceId: string) => {
    setValue(TRIGGER_FIELDS.deviceId, nextDeviceId, setValueOptions);
  };

  if (!devicesLoading && !showWhatsappWebOption && !hasWhatsappApi) {
    return (
      <Typography className={classes.emptyState}>
        {hasWhatsappWeb && !enableWhatsappWebProvider
          ? 'No hay dispositivos de WhatsApp API conectados. Conecta WhatsApp API para iniciar conversaciones.'
          : 'No hay dispositivos de WhatsApp conectados. Conecta WhatsApp Web o WhatsApp API para iniciar conversaciones.'}
      </Typography>
    );
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.label}>Proveedor</Typography>
      <div className={classes.options}>
        {showWhatsappWebOption && (
          <TiendadaRadioButton
            name="trigger-init-provider"
            label="WhatsApp Web"
            description="Mensaje de texto con archivos adjuntos"
            checked={provider === 'WHATSAPP_WEB'}
            onChange={checked => {
              if (checked) handleProviderChange('WHATSAPP_WEB');
            }}
          />
        )}
        {hasWhatsappApi && (
          <TiendadaRadioButton
            name="trigger-init-provider"
            label="WhatsApp API"
            description="Mensaje con template aprobado"
            checked={provider === 'WHATSAPP_API'}
            onChange={checked => {
              if (checked) handleProviderChange('WHATSAPP_API');
            }}
          />
        )}
      </div>
      {providerError && (
        <Typography className={classes.fieldError}>{providerError}</Typography>
      )}

      {provider && (
        <>
          <Typography className={classes.label}>Dispositivo</Typography>
          {devicesLoading ? (
            <Typography className={classes.emptyState}>
              Cargando dispositivos...
            </Typography>
          ) : devicesForProvider.length === 0 ? (
            <Typography className={classes.emptyState}>
              No hay dispositivos conectados para este proveedor.
            </Typography>
          ) : (
            <div className={classes.options}>
              {devicesForProvider.map((device: StoreDevice) => {
                const optionId = `trigger-init-device-${device.id}`;
                const isSelected = deviceId === device.id;

                return (
                  <React.Fragment key={device.id}>
                    <input
                      type="radio"
                      id={optionId}
                      name="trigger-init-device"
                      className={classes.deviceInput}
                      checked={isSelected}
                      onChange={() => handleDeviceSelect(device.id)}
                      hidden
                    />
                    <label htmlFor={optionId} className={classes.deviceOption}>
                      <span className={classes.deviceRadio} />
                      <picture className={classes.deviceIcon}>
                        <img
                          src={getMultichatServiceIcon(device.service)}
                          alt={device.service}
                        />
                      </picture>
                      <div className={classes.deviceInfo}>
                        <Typography className={classes.deviceName}>
                          {getServiceLabel(device.service)}
                        </Typography>
                        <Typography className={classes.deviceIdentifier}>
                          {formatStoreDeviceIdentifier(device)}
                        </Typography>
                      </div>
                    </label>
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {deviceError && (
            <Typography className={classes.fieldError}>
              {deviceError}
            </Typography>
          )}
        </>
      )}

      {provider === 'WHATSAPP_WEB' && <ResponseSection />}

      {provider === 'WHATSAPP_API' && (
        <>
          <Typography className={classes.label}>Template</Typography>
          <CustomSelect
            placeholder={
              templatesLoading
                ? 'Cargando templates...'
                : 'Seleccionar template'
            }
            value={templateName}
            onChange={event => {
              const nextTemplate = templates.find(
                template => template.name === event.target.value
              );
              setValue(
                TRIGGER_FIELDS.templateName,
                event.target.value,
                setValueOptions
              );
              setValue(
                TRIGGER_FIELDS.templateLang,
                nextTemplate?.language || '',
                setValueOptions
              );
              setValue(TRIGGER_FIELDS.templateComponents, [], setValueOptions);
            }}
            error={!!templateError}
          >
            {templates.map(template => (
              <MenuItem
                key={template.id || template.name}
                value={template.name}
              >
                {template.name}
              </MenuItem>
            ))}
          </CustomSelect>
          {templateError && (
            <Typography className={classes.fieldError}>
              {templateError}
            </Typography>
          )}

          {selectedTemplate && (
            <WhatsAppTemplateRenderer
              template={selectedTemplate}
              enabledVariables
              onChangeVariables={components => {
                setValue(
                  TRIGGER_FIELDS.templateComponents,
                  components || [],
                  setValueOptions
                );
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
